import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { warning } from '../../../../../components/Alert/Alert';
import Button from '../../../../../components/Button/Button';
import Input from '../../../../../components/Input/Input';
import { useAppDispatch, useAppSelector } from '../../../../../reduxService/hooks';
import { roundToFour } from '../../../../MACRA_1/components/InputForm/Geometry/utils';
import ModalSoilVelocity from '../../../../MACRA_1/components/InputForm/HydraulicData/ModalSoilVelocity/ModalSoilVelocity';
import { conversionLabel } from '../../../../conversion';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import { selectProject } from '../../../selector/conversion';
import { setHydraulicData } from '../../../store/project/projectSlice';
import { cleanResults } from '../../../store/project/resultsSlice';
import './HydraulicData.scss';
import { IHydraulicDataProps, IHydraulicDataSlopedWeirForm } from './IHydraulicData';

const HydraulicDataSlopedWeir = (props: IHydraulicDataProps) => {
    let timer = useRef<NodeJS.Timeout | null>(null);
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const project = useAppSelector(selectProject);
    const projectInfo = useAppSelector(selectProjectInfo);

    const defaultValue: IHydraulicDataSlopedWeirForm = {
        discharge: (project.data?.hydraulicData as IHydraulicDataSlopedWeirForm)?.discharge,
        roughnessCoefficient: (project.data!.hydraulicData as IHydraulicDataSlopedWeirForm)?.roughnessCoefficient,
        riverBedGradient: (project.data!.hydraulicData as IHydraulicDataSlopedWeirForm)?.riverBedGradient,
        soilAdmissibleVelocity: (project.data!.hydraulicData as IHydraulicDataSlopedWeirForm)?.soilAdmissibleVelocity,
        id: project.data?.hydraulicData?.id,
    };
    
    const [form, setForm] = useState<IHydraulicDataSlopedWeirForm>(defaultValue);
    const [open, setOpen] = useState<boolean>(false);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let name = e.target.name;
        let value = parseFloat(e.target.value);

        if (value < 0) {
            value = 0;
            warning(t('warning_not_be_lower', { value: 0 }));
        }

        setForm({
            ...form!,
            [name]: value,
        });
    };

    useEffect(() => {
        if (timer.current) {
            clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
            dispatch(cleanResults());
            dispatch(setHydraulicData(form as IHydraulicDataSlopedWeirForm));
        }, 1000);
    }, [form]);

    const handleClose = (velocity: number) => {
        setForm({
            ...form,
            soilAdmissibleVelocity: velocity,
        });
        setOpen(false);
    };

    const handleVelocityButton = (e: React.MouseEvent<HTMLButtonElement> | undefined) => {
        setOpen(true);
    };

    return (
        <div className='d-flex flex-column gap-2'>
            <Input
                type='number'
                title={t('input_discharge', { unit_measure: conversionLabel.m3s_ft3s[projectInfo.data?.unitOfMeasure as number] })}
                value={form.discharge}
                name='discharge'
                className='d-flex justify-content-between align-items-center'
                onChange={handleInputChange}
            />
            <Input
                type='number'
                title={t('input_roughnessCoefficient')}
                value={form.roughnessCoefficient}
                name='roughnessCoefficient'
                className='d-flex justify-content-between align-items-center'
                onChange={handleInputChange}
            />
            <Input
                type='number'
                title={t('input_riverBedGradient', { unit_measure: 'm/m' })}
                value={form.riverBedGradient}
                name='riverBedGradient'
                className='d-flex justify-content-between align-items-center'
                onChange={handleInputChange}
            />
            <div className='d-flex align-items-center'>
                <Input
                    type='number'
                    step={0.01}
                    title={t('hydraulic_data_soil_velocity', { unit_measure: conversionLabel.ms_fts[projectInfo.data?.unitOfMeasure as number] })}
                    name='soilAdmissibleVelocity'
                    className='d-flex justify-content-between align-items-center'
                    classNameInput='velocityText'
                    value={form.soilAdmissibleVelocity}
                    onChange={e => handleClose(roundToFour(parseFloat(e.target.value)))}
                />
                <Button classNameBtn='btn-velocity' onClick={handleVelocityButton}>
                    <i className='fa-solid fa-sliders fs-6' />
                </Button>

                <ModalSoilVelocity open={open} onClose={handleClose} />
            </div>
        </div>
    );
};

export default HydraulicDataSlopedWeir;
